<template>
  <div>
    <text-box :label="title" icon="chart-bar" :hideTitle="hideTitle">
      <template v-slot:info>
        <percent-chip v-if="precision" :value="precision" :best="100" icon="mdi-bullseye-arrow"/>
        <slot name="info"></slot>
      </template>
      <v-container fluid>
        <v-row>
          <v-col cols="12">
            <v-chart :option="options" :init-options="{renderer: 'canvas'}" autoresize :style="`height: ${chartHeight}px; width: 100%;`"/>
          </v-col>
        </v-row>
      </v-container>
    </text-box>
  </div>
</template>

<script>
import ECharts from 'vue-echarts'
import 'echarts/lib/chart/bar'
import 'echarts/lib/component/tooltip'
import 'echarts/lib/component/toolbox'
import 'echarts/lib/component/legend'
import 'echarts/lib/component/legendScroll'
import 'echarts/lib/component/dataZoom'
import 'echarts/lib/chart/line'
import { graphic } from 'echarts/core'
// import 'zrender/lib/svg/svg'
import 'zrender/lib/canvas/canvas'
import numbro from 'numbro'
import moment from 'moment'
import TextBox from '@/components/TextBox.vue'
import PercentChip from '@/components/PercentChip.vue'

export default {
  data () {
    return {}
  },
  computed: {
    options () {
      return {
        animation: this.animation,
        grid: {
          top: 10,
          left: 50,
          right: this.showLegend && !this.legendIsHorizontal ? Math.max(this.$i18n.locale === 'es' || this.$i18n.locale === 'pt' ? 170 : 150, this.longestLabelLength * 10) : 0,
          bottom: this.gridBottom,
          containLabel: true
        },
        tooltip: {
          trigger: 'axis',
          formatter: this.formatTooltip
        },
        toolbox: {
          show: this.export,
          left: 0,
          feature: {
            dataView: {
              readOnly: true,
              title: this.$t('components.StackedBarChart.dataView'),
              lang: [this.$t('components.StackedBarChart.dataView'), this.$t('components.StackedBarChart.close'), this.$t('components.StackedBarChart.refresh')]
            },
            saveAsImage: {
              type: 'png',
              name: this.exportImageName || this.title || 'ExportedImage',
              title: this.$t('components.StackedBarChart.saveAsImage')
            }
          },
          showTitle: false,
          tooltip: {
            show: true,
            formatter: function (param) {
              return param.title
            }
          }
        },
        legend: this.legend,
        xAxis: {
          type: 'category',
          name: this.xName,
          nameLocation: 'center',
          nameGap: 30,
          splitLine: {
            show: false
          },
          axisLabel: {
            formatter: this.xAxisLabelFormatter,
            // Initials need no rotation: one character stands under its bar however many bars there
            // are, and upright is what makes them readable as a row.
            rotate: this.xAxisLabelInitials ? 0 : 75
          },
          axisTick: {
            show: false
          },
          data: this.seriesKeys[0] in this.data ? this.data[this.seriesKeys[0]].x : null
        },
        yAxis: this.yAxis,
        series: this.series,
        dataZoom: [
          {
            type: 'slider',
            start: 0,
            end: 100,
            show: this.showDataZoom,
            labelFormatter: this.dataZoomLabelFormatter
          }
        ]
      }
    },
    seriesKeys () {
      return Object.keys(this.data)
    },
    legendIsHorizontal () {
      return this.legendOrient === 'horizontal'
    },
    // Vertical is the chart's own shape: a column down the right, with the grid narrowed by
    // longestLabelLength to leave room for it. Horizontal lays the same scrolling list under the
    // plot instead, which gives the bars the full width back — the choice a caller makes when the
    // plot matters more than reading every series name at a glance.
    legend () {
      const legend = {
        show: this.showLegend,
        data: this.seriesKeys.map(key => this.prettyNames && this.prettyNames[key] ? this.prettyNames[key] : key),
        type: 'scroll',
        orient: this.legendOrient,
        selector: [
          {
            type: 'all',
            title: this.$t('components.StackedBarChart.selectAll')
          },
          {
            type: 'inverse',
            title: this.$t('components.StackedBarChart.invert')
          }
        ]
      }
      if (this.legendIsHorizontal) {
        // Left rather than centred: the selector buttons sit at the far end of a horizontal legend,
        // and a centred list slides them under the plot's right edge as channels are added.
        legend.left = 0
        legend.right = 60
        // Under the plot, and above the dataZoom slider when there is one — gridBottom leaves the
        // band this stands in.
        legend.bottom = this.plotBottomMargin
      } else {
        legend.right = 0
        legend.top = 0
      }
      return legend
    },
    // What the grid gives up at the bottom to whatever stands below the plot: the dataZoom slider,
    // and the legend when it is laid horizontally under the bars.
    plotBottomMargin () {
      return this.showDataZoom ? Math.max(this.gridBottomMargin || 0, 40) : 0
    },
    gridBottom () {
      return this.plotBottomMargin + (this.showLegend && this.legendIsHorizontal ? 30 : 0)
    },
    // The axis draws dates through formatDate, initials through their first character, and anything
    // else as it comes. The category itself is untouched either way, so the tooltip still names the
    // month in full.
    xAxisLabelFormatter () {
      if (this.xAxisLabelInitials) {
        return value => String(value).slice(0, 1)
      }
      return this.xAxisTypeIsDate ? this.formatDate : null
    },
    yAxis () {
      const mainAxis = {
        type: 'value',
        name: this.yName,
        nameLocation: 'middle',
        nameGap: 50,
        scale: this.scale,
        splitLine: {
          lineStyle: {
            type: 'dashed'
          }
        },
        axisLabel: {
          formatter: this.formatValue
        }
      }
      if (!this.showDataZoom) {
        return mainAxis
      }
      // The invisible __netSumForDataZoom line (which feeds the dataZoom slider
      // preview) lives on this hidden secondary axis so it never contributes to
      // the main axis extent. Otherwise its always-visible net-sum values would
      // pin the scaled main axis, preventing it from adapting when a bar series
      // is toggled off in the legend.
      return [mainAxis, {
        type: 'value',
        scale: this.scale,
        show: false,
        splitLine: {
          show: false
        }
      }]
    },
    series () {
      let series = []
      if (this.showDataZoom) {
        series.push({
          name: '__netSumForDataZoom',
          type: 'line',
          yAxisIndex: 1,
          data: this.netSumValues,
          showSymbol: false,
          lineStyle: { opacity: 0 },
          silent: true,
          tooltip: { show: false },
          animation: false
        })
      }
      this.seriesKeys.forEach(key => {
        series.push({
          name: this.prettyNames && this.prettyNames[key] ? this.prettyNames[key] : key,
          type: 'bar',
          stack: 'stack',
          data: this.data[key].y.map(datapoint => {
            return {
              value: datapoint,
              itemStyle: {
                borderRadius: datapoint > 0 ? [1, 1, 0, 0] : [0, 0, 1, 1]
              }
            }
          }),
          itemStyle: {
            color: new graphic.LinearGradient(0, 3, 0, 0, [
              { offset: 0, color: 'white' },
              { offset: 1, color: this.colors ? this.colors[this.seriesKeys.indexOf(key) % this.colors.length] : (this.color ? this.color : 'blue') }
            ])
          }
        })
      })
      return series
    },
    netSumValues () {
      if (!this.seriesKeys.length) {
        return []
      }
      const firstKey = this.seriesKeys[0]
      if (!this.data[firstKey] || !this.data[firstKey].y) {
        return []
      }
      const len = this.data[firstKey].y.length
      const sums = new Array(len).fill(0)
      this.seriesKeys.forEach(key => {
        const ys = this.data[key] && this.data[key].y
        if (Array.isArray(ys)) {
          for (let i = 0; i < len && i < ys.length; i++) {
            const v = ys[i]
            if (typeof v === 'number' && !isNaN(v)) {
              sums[i] += v
            }
          }
        }
      })
      return sums
    },
    longestLabelLength: function () {
      var longest = []
      if (this.seriesKeys.length > 0) {
        longest = this.seriesKeys.reduce(
          function (a, b) {
            return a.length > b.length ? a : b
          }
        )
      }
      return longest.length
    }
  },
  methods: {
    formatTooltip: function (params) {
      const filteredParams = params.filter(p => p.seriesName !== '__netSumForDataZoom')
      if (!filteredParams.length) {
        return ''
      }
      var tooltip = `<b>${this.xAxisTypeIsDate ? this.formatDate(filteredParams[0].name) : filteredParams[0].name}</b><br><br>`
      let total = 0
      for (let index = filteredParams.length - 1; index >= 0; index--) {
        const param = filteredParams[index]
        const color = (param.color && param.color.colorStops && param.color.colorStops.length > 1 && param.color.colorStops[1] && param.color.colorStops[1].color)
          ? param.color.colorStops[1].color
          : (param.color || '#999999')
        tooltip += `
          <b style="color: ${color}">▶</b>
          ${param.seriesName}:
          <span style="color: lightblue; font-size: 16px;">${this.formatValue(param.value)}${this.sumOneHundred ? '%' : ''}</span>
          <br>
        `
        if (typeof param.value === 'number' && !isNaN(param.value)) {
          total += param.value
        }
      }
      tooltip += `<hr style="border: none; border-top: 1px solid rgba(255, 255, 255, 0.3); margin: 6px 0;">`
      tooltip += `
        <b>Total:</b>
        <span style="color: lightblue; font-size: 16px;">${this.formatValue(total)}${this.sumOneHundred ? '%' : ''}</span>
      `
      return tooltip
    },
    formatValue: function (value) {
      if (value && !isNaN(value)) {
        return numbro(value).format({
          average: true,
          lowPrecision: false,
          mantissa: 1,
          optionalMantissa: true
        })
      } else {
        return '–'
      }
    },
    formatDate (date) {
      if (this.period === 'month') {
        return moment(date).format('MMM \'YY')
      } else {
        return moment(date).format('\'YY-MMM-DD')
      }
    },
    dataZoomLabelFormatter (value) {
      const firstKey = this.seriesKeys[0]
      if (!firstKey || !this.data[firstKey] || !this.data[firstKey].x) {
        return ''
      }
      const xs = this.data[firstKey].x
      const idx = Math.max(0, Math.min(xs.length - 1, Math.round(value / 100 * (xs.length - 1))))
      const raw = xs[idx]
      return this.xAxisTypeIsDate ? this.formatDate(raw) : String(raw)
    },
    formatEchartsValue: function (obj) {
      return `${this.formatValue(obj.value)}`
    }
  },
  props: {
    data: {
      type: Object,
      required: true
    },
    title: {
      type: String,
      required: false,
      default () {
        return ''
      }
    },
    exportImageName: {
      type: String,
      required: false
    },
    icon: {
      type: String,
      required: false,
      default: function () {
        return ''
      }
    },
    xName: {
      type: String,
      required: false
    },
    yName: {
      type: String,
      required: false
    },
    precision: {
      type: Number,
      required: false
    },
    xAxisTypeIsDate: {
      type: Boolean,
      required: false
    },
    xAxisRotate: {
      type: Number,
      required: false
    },
    gridBottomMargin: {
      type: Number,
      required: false
    },
    export: {
      type: Boolean,
      required: false,
      default: function () {
        return false
      }
    },
    period: {
      type: String,
      required: false,
      default: function () {
        return 'month'
      }
    },
    colors: {
      type: Array,
      required: false,
      default: function () {
        return null
      }
    },
    color: {
      type: String,
      required: false,
      default: function () {
        return null
      }
    },
    animation: {
      type: Boolean,
      required: false,
      default () {
        return true
      }
    },
    showLegend: {
      type: Boolean,
      required: false,
      default () {
        return true
      }
    },
    legendOrient: {
      type: String,
      required: false,
      validator (value) {
        return ['vertical', 'horizontal'].indexOf(value) !== -1
      },
      default () {
        return 'vertical'
      }
    },
    xAxisLabelInitials: {
      type: Boolean,
      required: false,
      default () {
        return false
      }
    },
    hideTitle: {
      type: Boolean,
      required: false,
      default () {
        return false
      }
    },
    prettyNames: {
      type: Object,
      required: false,
      default () {
        return {}
      }
    },
    showDataZoom: {
      type: Boolean,
      required: false,
      default () {
        return false
      }
    },
    scale: {
      type: Boolean,
      required: false,
      default () {
        return false
      }
    },
    // The plot area's height in pixels. Defaults to what every existing call site already gets; a
    // caller passes it to line this chart's box up with another chart's beside it.
    chartHeight: {
      type: Number,
      required: false,
      default () {
        return 320
      }
    }
  },
  components: {
    'v-chart': ECharts,
    TextBox,
    PercentChip
  }
}
</script>
